// شاشة الإعداد الأولي — رحلة مسار: منصب/جهة أو مسار/مستوى، ثم عرض البرامج المطابقة للمصفوفة
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BrandLockup from "@/v2/components/BrandLockup";
import ProfilePicker from "@/masar/components/ProfilePicker";
import { errText } from "@/v2/lib/hooks";
import "@/v2/v2.css";
import { Sparkles, ArrowLeft, Send, Compass } from "lucide-react";

const Steps = ({ step }) => (
  <div className="flex items-center gap-2 mb-8" data-testid="onb-progress">
    {[0, 1, 2].map((i) => (
      <div key={i} className="h-1.5 rounded-full transition-all duration-500"
        style={{ width: i === step ? 44 : 22, background: i <= step ? "#bb9661" : "#e3e7ea" }} />
    ))}
  </div>
);

export default function Onboarding() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(user?.masar?.profile_id || user?.masar?.status === "needs_completion" ? 2 : 0);
  const [picked, setPicked] = useState(null);
  const [sending, setSending] = useState(false);

  const submit = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/auth/onboarding/masar");
      await refresh();
      navigate(data.status === "pending" ? "/pending" : "/masar/plan", { replace: true });
    } catch (e) { toast.error(errText(e, "تعذّر تجهيز المسار")); } finally { setSending(false); }
  };
  const requiresApproval = picked?.requires_approval || (!picked && !!user?.masar?.position_key);
  const needsCompletion = picked?.needs_completion || (!picked && user?.masar?.status === "needs_completion");

  return (
    <div className="v2-root min-h-screen flex flex-col items-center justify-center p-5 sm:p-8 v2-grid v2-pattern relative overflow-hidden" dir="rtl">
      <div className="absolute top-[-15%] left-[-10%] w-[520px] h-[520px] rounded-full bg-ok/12 blur-3xl v2-float pointer-events-none" />
      <div className="absolute bottom-[-15%] right-[-5%] w-[420px] h-[420px] rounded-full bg-beige/10 blur-3xl v2-float pointer-events-none" style={{ animationDelay: "2s" }} />
      <div className="w-full max-w-2xl relative z-10">
        <div className="flex items-center justify-between gap-5 flex-wrap mb-8">
          <BrandLockup tone="dark" />
          <button onClick={() => { logout(); navigate("/login"); }} data-testid="onb-logout" className="text-xs text-ink-2 hover:text-ink">تسجيل الخروج</button>
        </div>
        <div className="v2-glass v2-glass-gold p-6 sm:p-10 relative overflow-hidden" data-testid="onboarding-card">
          <>
              <Steps step={step} />
              <AnimatePresence mode="wait">
                {step === 0 && (
                  <motion.div key="s0" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.35 }}>
                    <div className="inline-flex mb-5"><span className="v2-glass px-4 py-1.5 rounded-full text-xs text-ok border-ok/30 flex items-center gap-2"><Sparkles size={14} /> خطوتان قصيرتان قبل البداية</span></div>
                    <h1 className="text-3xl sm:text-4xl font-extrabold mb-4">مرحبًا بك{user?.name ? `، ${user.name.split(" ")[0]}` : ""}</h1>
                    <p className="text-ink-2 text-base leading-relaxed mb-8 max-w-lg">اختر إدارتك ومسمّاك الوظيفي لنعرض لك الدورات المطلوبة مباشرة من مصفوفة الجدارات.</p>
                    <button onClick={() => setStep(1)} data-testid="onb-start" className="v2-btn">لنبدأ <ArrowLeft size={18} /></button>
                  </motion.div>
                )}
                {step === 1 && (
                  <motion.div key="s1" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.35 }}>
                    <div className="flex items-center gap-3 mb-2"><span className="w-10 h-10 rounded-2xl bg-beige/15 text-beige-ink flex items-center justify-center"><Compass size={20} /></span><h2 className="text-2xl font-extrabold">حدّد ملفك الوظيفي</h2></div>
                    <p className="text-ink-2 text-sm mb-6">اختر منصبك وإدارتك ومسمّاك الوظيفي من المصفوفة.</p>
                    <ProfilePicker compact onDone={(d) => { setPicked(d); setStep(2); }} />
                  </motion.div>
                )}
                {step === 2 && (
                  <motion.div key="s2" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.35 }}>
                    <h2 className="text-2xl font-extrabold mb-2">{requiresApproval ? "تأكيد طلب المنصب" : "مسارك جاهز للبداية"}</h2>
                    <div className="v2-inset p-4 text-sm mb-5" data-testid="onb-summary">
                      {picked?.needs_completion || (!picked && user?.masar?.status === "needs_completion")
                        ? <span className="text-beige-ink">أُرسل طلب استكمال للإدارة. ستظهر دوراتك بعد ربط حسابك بملفك الوظيفي.</span>
                        : <>ملفك الوظيفي: <b className="v2-gold-text">{picked?.profile || user?.job_title}</b></>}
                    </div>
                    {requiresApproval && <p className="text-sm text-ink-2 mb-5">يتطلب حساب صاحب المنصب موافقة إدارة المنصة. بعد الاعتماد يظهر مسارك واطلاعك على الجهات والموظفين التابعين لك في مسار وتميز.</p>}
                    <div className="flex items-center justify-between">
                      <button onClick={() => setStep(1)} className="v2-btn-ghost v2-btn-sm" data-testid="onb-back-1">تغيير الملف الوظيفي</button>
                      <button onClick={submit} disabled={sending || needsCompletion} data-testid="onb-submit" className={`v2-btn ${sending ? "opacity-40 cursor-not-allowed" : ""}`}><Send size={17} /> {sending ? "جارٍ التجهيز..." : requiresApproval ? "إرسال للاعتماد" : "عرض مساري"}</button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
          </>
        </div>
      </div>
    </div>
  );
}
